import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, Calendar, GripVertical } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface PipelineContentProps {
  animationDelay?: number;
}

const pipelineStages = [
  { name: 'New Lead', count: 12, value: '$24,500', color: '#6B7280' },
  { name: 'Contacted', count: 8, value: '$18,200', color: '#3B82F6' },
  { name: 'Meeting Set', count: 5, value: '$32,000', color: '#8B5CF6' },
  { name: 'Proposal', count: 3, value: '$45,000', color: '#F59E0B' },
  { name: 'Won', count: 7, value: '$89,500', color: '#10B981' },
];

const generateLeads = (stageIndex: number) => [
  { name: 'Sarah Johnson', source: 'Website', value: '$4,500', avatar: 'SJ' },
  { name: 'Mike Chen', source: 'Referral', value: '$8,200', avatar: 'MC' },
  { name: 'Emma Wilson', source: 'LinkedIn', value: '$3,800', avatar: 'EW' },
];

const PipelineContent: React.FC<PipelineContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [dragPhase, setDragPhase] = useState<'resting' | 'lifting' | 'hover-source' | 'moving' | 'hover-target' | 'dropping' | 'bounce' | 'complete'>('resting');
  const [rippleActive, setRippleActive] = useState(false);

  // Enhanced multi-phase animation with smoother transitions
  useEffect(() => {
    const phases: Array<{ phase: typeof dragPhase; duration: number }> = [
      { phase: 'resting', duration: 1800 },
      { phase: 'lifting', duration: 350 },
      { phase: 'hover-source', duration: 200 },
      { phase: 'moving', duration: 600 },
      { phase: 'hover-target', duration: 250 },
      { phase: 'dropping', duration: 200 },
      { phase: 'bounce', duration: 150 },
      { phase: 'complete', duration: 1200 },
    ];
    
    let currentIndex = 0;
    let timeoutId: NodeJS.Timeout;
    
    const runAnimation = () => {
      const { phase, duration } = phases[currentIndex];
      setDragPhase(phase);
      
      if (phase === 'dropping') {
        setRippleActive(true);
        setTimeout(() => setRippleActive(false), 400);
      }
      
      timeoutId = setTimeout(() => {
        currentIndex = (currentIndex + 1) % phases.length;
        runAnimation();
      }, duration);
    };
    
    const initialTimeout = setTimeout(() => {
      runAnimation();
    }, animationDelay + 800);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
    };
  }, [animationDelay]);

  const getDragCardStyle = (): React.CSSProperties => {
    const baseTransition = 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)';
    const moveTransition = 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    
    switch (dragPhase) {
      case 'resting':
        return { 
          left: 'calc(20% + 4px)', 
          top: '72px',
          transform: 'rotate(0deg) scale(1) translateY(0)',
          boxShadow: 'none',
          zIndex: 1,
          opacity: 0,
          transition: 'none'
        };
      case 'lifting':
        return { 
          left: 'calc(20% + 4px)', 
          top: '64px',
          transform: 'rotate(-3deg) scale(1.08) translateY(-4px)',
          boxShadow: isDark 
            ? '0 12px 28px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0,0,0,0.4)' 
            : '0 12px 28px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
          opacity: 1,
          transition: baseTransition
        };
      case 'hover-source':
        return { 
          left: 'calc(20% + 4px)', 
          top: '60px',
          transform: 'rotate(-2deg) scale(1.1) translateY(-6px)',
          boxShadow: isDark 
            ? '0 16px 32px rgba(59, 130, 246, 0.35), 0 6px 16px rgba(0,0,0,0.5)' 
            : '0 16px 32px rgba(59, 130, 246, 0.25), 0 6px 16px rgba(0,0,0,0.12)',
          zIndex: 100,
          opacity: 1,
          transition: baseTransition
        };
      case 'moving':
        return { 
          left: 'calc(40% + 4px)', 
          top: '58px',
          transform: 'rotate(2deg) scale(1.08) translateY(-4px)',
          boxShadow: isDark 
            ? '0 20px 40px rgba(139, 92, 246, 0.3), 0 8px 20px rgba(0,0,0,0.5)' 
            : '0 20px 40px rgba(139, 92, 246, 0.2), 0 8px 20px rgba(0,0,0,0.1)',
          zIndex: 100,
          opacity: 1,
          transition: moveTransition
        };
      case 'hover-target':
        return { 
          left: 'calc(40% + 4px)', 
          top: '62px',
          transform: 'rotate(1deg) scale(1.06) translateY(-2px)',
          boxShadow: isDark 
            ? '0 14px 30px rgba(139, 92, 246, 0.35), 0 5px 14px rgba(0,0,0,0.45)' 
            : '0 14px 30px rgba(139, 92, 246, 0.25), 0 5px 14px rgba(0,0,0,0.1)',
          zIndex: 100,
          opacity: 1,
          transition: baseTransition
        };
      case 'dropping':
        return { 
          left: 'calc(40% + 4px)', 
          top: '72px',
          transform: 'rotate(0deg) scale(0.98) translateY(2px)',
          boxShadow: isDark 
            ? '0 4px 12px rgba(139, 92, 246, 0.2)' 
            : '0 4px 12px rgba(139, 92, 246, 0.15)',
          zIndex: 100,
          opacity: 1,
          transition: 'all 0.2s cubic-bezier(0.55, 0.055, 0.675, 0.19)'
        };
      case 'bounce':
        return { 
          left: 'calc(40% + 4px)', 
          top: '72px',
          transform: 'rotate(0deg) scale(1.04) translateY(-2px)',
          boxShadow: isDark 
            ? '0 6px 16px rgba(139, 92, 246, 0.25)' 
            : '0 6px 16px rgba(139, 92, 246, 0.18)',
          zIndex: 100,
          opacity: 1,
          transition: 'all 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
        };
      case 'complete':
        return { 
          left: 'calc(40% + 4px)', 
          top: '72px',
          transform: 'rotate(0deg) scale(1) translateY(0)',
          boxShadow: 'none',
          zIndex: 1,
          opacity: 1,
          transition: baseTransition
        };
      default:
        return { left: 'calc(20% + 4px)', top: '72px', transform: 'rotate(0deg) scale(1)', zIndex: 1, opacity: 0 };
    }
  };

  const isCardDragging = dragPhase !== 'resting' && dragPhase !== 'complete';
  const showGhostInSource = dragPhase !== 'resting' && dragPhase !== 'complete';
  const showGhostInTarget = dragPhase === 'moving' || dragPhase === 'hover-target';
  const highlightSourceColumn = dragPhase === 'lifting' || dragPhase === 'hover-source';
  const highlightTargetColumn = dragPhase === 'moving' || dragPhase === 'hover-target' || dragPhase === 'dropping';

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden relative"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Sales Pipeline
          </span>
          <span 
            className="text-[8px] px-1.5 py-0.5 rounded-full"
            style={{ 
              backgroundColor: isDark ? '#10B98120' : '#10B98115',
              color: '#10B981'
            }}
          >
            $209,200 total
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div 
            className="text-[8px] px-2 py-1 rounded"
            style={{ 
              backgroundColor: isDark ? '#3B82F620' : '#3B82F610',
              color: '#3B82F6'
            }}
          >
            + Add Lead
          </div>
        </div>
      </div>

      {/* Pipeline Columns */}
      <div 
        className="flex-1 flex gap-0.5 lg:gap-1 p-1 lg:p-2 overflow-hidden relative"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {pipelineStages.map((stage, stageIndex) => {
          const isSourceColumn = stageIndex === 1;
          const isTargetColumn = stageIndex === 2;
          const columnHighlighted = (isSourceColumn && highlightSourceColumn) || (isTargetColumn && highlightTargetColumn);
          
          return (
            <div
              key={stageIndex}
              className="flex-1 flex flex-col min-w-0 rounded-lg overflow-hidden relative"
              style={{ 
                backgroundColor: isDark ? '#222222' : '#FFFFFF',
                border: `1px solid ${columnHighlighted ? stage.color : (isDark ? '#333333' : '#E5E5E5')}`,
                boxShadow: columnHighlighted 
                  ? `0 0 0 2px ${stage.color}30, inset 0 0 20px ${stage.color}10` 
                  : 'none',
                animation: `scaleIn 0.3s ease-out ${animationDelay + 250 + (stageIndex * 80)}ms both`,
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
              }}
            >
              {/* Column Header */}
              <div 
                className="px-1 lg:px-2 py-1 lg:py-1.5 border-b"
                style={{ borderColor: isDark ? '#333333' : '#EEEEEE' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-0.5 lg:gap-1">
                    <div 
                      className="w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full"
                      style={{ 
                        backgroundColor: stage.color,
                        boxShadow: columnHighlighted ? `0 0 8px ${stage.color}` : 'none',
                        transition: 'box-shadow 0.3s ease'
                      }}
                    />
                    <span 
                      className="text-[6px] lg:text-[8px] font-medium truncate"
                      style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                    >
                      {stage.name}
                    </span>
                  </div>
                  <span 
                    className="text-[5px] lg:text-[7px] px-0.5 lg:px-1 py-0.5 rounded hidden sm:block"
                    style={{ 
                      backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0',
                      color: isDark ? '#888888' : '#666666'
                    }}
                  >
                    {stage.count}
                  </span>
                </div>
                <div 
                  className="text-[5px] lg:text-[7px] mt-0.5 hidden sm:block"
                  style={{ color: stage.color }}
                >
                  {stage.value}
                </div>
              </div>

              {/* Lead Cards */}
              <div className="flex-1 p-0.5 lg:p-1 space-y-0.5 lg:space-y-1 overflow-hidden">
                {/* Ghost placeholder in source column */}
                {isSourceColumn && showGhostInSource && (
                  <div 
                    className="rounded border-2 border-dashed"
                    style={{ 
                      height: '44px',
                      borderColor: highlightSourceColumn ? '#3B82F6' : (isDark ? '#444444' : '#CCCCCC'),
                      backgroundColor: isDark ? '#1A1A1A' : '#F9F9F9',
                      transition: 'all 0.3s ease',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}
                  />
                )}

                {/* Ghost placeholder in target column */}
                {isTargetColumn && showGhostInTarget && (
                  <div 
                    className="rounded border-2 border-dashed relative overflow-hidden"
                    style={{ 
                      height: '44px',
                      borderColor: '#8B5CF6',
                      backgroundColor: isDark ? '#8B5CF610' : '#8B5CF608',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Shimmer effect */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(90deg, transparent, ${isDark ? '#8B5CF620' : '#8B5CF615'}, transparent)`,
                        animation: 'shimmer 1.2s ease-in-out infinite'
                      }}
                    />
                  </div>
                )}

                {/* Drop ripple effect in target column */}
                {isTargetColumn && rippleActive && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${stage.color}30 0%, transparent 70%)`,
                      animation: 'ripple 0.4s ease-out forwards'
                    }}
                  />
                )}

                {generateLeads(stageIndex)
                  .slice(isSourceColumn && isCardDragging ? 1 : 0, 2)
                  .map((lead, leadIndex) => (
                  <div
                    key={leadIndex}
                    className="p-1.5 rounded cursor-grab group"
                    style={{ 
                      backgroundColor: isDark ? '#1A1A1A' : '#F9F9F9',
                      border: `1px solid ${isDark ? '#2A2A2A' : '#EEEEEE'}`,
                      animation: `fadeIn 0.3s ease-out ${animationDelay + 400 + (stageIndex * 80) + (leadIndex * 50)}ms both`
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-1">
                        <GripVertical 
                          className="w-2 h-2 opacity-0 group-hover:opacity-50 transition-opacity" 
                          style={{ color: isDark ? '#666666' : '#999999' }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full flex items-center justify-center text-[6px] font-medium"
                          style={{ 
                            backgroundColor: `${stage.color}20`,
                            color: stage.color
                          }}
                        >
                          {lead.avatar}
                        </div>
                        <div>
                          <div 
                            className="text-[7px] font-medium truncate max-w-[50px]"
                            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                          >
                            {lead.name}
                          </div>
                          <div 
                            className="text-[6px] truncate"
                            style={{ color: isDark ? '#666666' : '#999999' }}
                          >
                            {lead.source}
                          </div>
                        </div>
                      </div>
                      <span 
                        className="text-[6px] font-medium"
                        style={{ color: '#10B981' }}
                      >
                        {lead.value}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      {[Phone, MessageCircle, Mail, Calendar].map((Icon, i) => (
                        <Icon 
                          key={i}
                          className="w-2 h-2"
                          style={{ color: isDark ? '#444444' : '#CCCCCC' }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Animated Drag Card */}
        <div
          className="absolute rounded cursor-grabbing"
          style={{
            width: 'calc(20% - 8px)',
            ...getDragCardStyle(),
            backgroundColor: isDark ? '#1A1A1A' : '#F9F9F9',
            border: `2px solid ${isCardDragging ? (dragPhase === 'moving' || dragPhase === 'hover-target' ? '#8B5CF6' : '#3B82F6') : (isDark ? '#2A2A2A' : '#EEEEEE')}`,
            padding: '6px',
          }}
        >
          {/* Card glow effect */}
          {isCardDragging && (
            <div 
              className="absolute inset-0 rounded pointer-events-none"
              style={{
                background: dragPhase === 'moving' || dragPhase === 'hover-target'
                  ? `radial-gradient(ellipse at center, #8B5CF615 0%, transparent 70%)`
                  : `radial-gradient(ellipse at center, #3B82F615 0%, transparent 70%)`,
                transition: 'background 0.3s ease'
              }}
            />
          )}
          
          <div className="flex items-start justify-between relative z-10">
            <div className="flex items-center gap-1">
              <GripVertical 
                className="w-2 h-2" 
                style={{ 
                  color: isCardDragging ? '#3B82F6' : (isDark ? '#666666' : '#999999'),
                  transition: 'color 0.2s ease'
                }}
              />
              <div 
                className="w-4 h-4 rounded-full flex items-center justify-center text-[6px] font-medium"
                style={{ 
                  backgroundColor: '#3B82F620',
                  color: '#3B82F6'
                }}
              >
                AK
              </div>
              <div>
                <div 
                  className="text-[7px] font-medium"
                  style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                >
                  Alex Kim
                </div>
                <div 
                  className="text-[6px]"
                  style={{ color: isDark ? '#666666' : '#999999' }}
                >
                  Website
                </div>
              </div>
            </div>
            <span className="text-[6px] font-medium" style={{ color: '#10B981' }}>
              $12,000
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1 relative z-10">
            {[Phone, MessageCircle, Mail, Calendar].map((Icon, i) => (
              <Icon 
                key={i}
                className="w-2 h-2"
                style={{ color: isDark ? '#444444' : '#CCCCCC' }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Stats */}
      <div 
        className="flex items-center justify-between px-3 py-1.5 border-t"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 600}ms both`
        }}
      >
        <div className="flex items-center gap-3">
          <div className="text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>
            <span style={{ color: '#10B981' }}>↑ 23%</span> conversion rate
          </div>
          <div className="text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>
            Avg deal: <span style={{ color: isDark ? '#FFFFFF' : '#111111' }}>$5,980</span>
          </div>
        </div>
        <div 
          className="text-[7px] px-1.5 py-0.5 rounded"
          style={{ 
            backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0',
            color: isDark ? '#888888' : '#666666'
          }}
        >
          35 leads total
        </div>
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes ripple {
          0% { opacity: 1; transform: scale(0.8); }
          100% { opacity: 0; transform: scale(1.2); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default PipelineContent;
